import { format } from 'date-fns';
import type { NoteShiftChange, RelatedNote } from '../../hooks/types';
import {
  asDateNumber,
  shiftDateTimeFormat,
  TIMEZONE,
} from '../../lib/utils/date-time';
import type { RelatedNoteWithCreatedAtAndUpdatedAtAsNumber } from './types';
import { formatInTimeZone } from 'date-fns-tz';
import { compact } from '../../lib/utils';

export const toNoteContent = (content: string | undefined) =>
  content === undefined ? undefined : { content };

const relatedNoteToRelatedNoteWithCreatedAtAndUpdatedAtAsNumber = (
  relatedNote: RelatedNote
): RelatedNoteWithCreatedAtAndUpdatedAtAsNumber => ({
  ...relatedNote,
  createdAt: asDateNumber(relatedNote.createdAt),
  updatedAt: asDateNumber(relatedNote.updatedAt),
});
/**
 * Converts an array of Action RelatedNotes into a format that can be
 * used by the Note component.
 *
 * @param relatedNotes - an array of Action RelatedNotes.
 * @returns an array of Action RelatedNotes that can be used by the Note component.
 */
export const relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber = (
  relatedNotes: readonly RelatedNote[]
): readonly RelatedNoteWithCreatedAtAndUpdatedAtAsNumber[] => {
  return compact(relatedNotes).map(
    relatedNoteToRelatedNoteWithCreatedAtAndUpdatedAtAsNumber
  );
};

/**
 * Validates a note.
 *
 * @param note - note to validate.
 * @returns a validation result.
 */
export const noteValidator = (
  note: string | undefined
):
  | { readonly isValid: false }
  | { readonly isValid: true; readonly note: string } => {
  const trimmed = (note ?? '').trim();
  const isValid = trimmed.length > 0;
  return isValid ? { isValid: true, note: trimmed } : { isValid: false };
};

export const validNotesOrUndefined = (notes: string) => {
  return noteValidator(notes).isValid ? notes : undefined;
};

export const defaultIfEmpty = (
  value: string | undefined,
  defaultValue = '-'
) => (value === undefined || value === '' ? defaultValue : value);

/**
 * Converts the note content and shift changes to a string.
 *
 * NOTE: {@link noteToString} should be preferred to this function wherever possible,
 * since it will also provide the author name and edited at timestamp; this function is currently only
 * intended to be used for exporting notes from the event objects in the PRAS CSV exporter.
 *
 * @param changes - shift changes
 * @param noteContent - note content
 * @returns a string representation of the note content and shift changes.
 */
export const noteContentAndShiftChangesToString = (
  changes: readonly NoteShiftChange[],
  noteContent: string | undefined
) => {
  const changeStrings = changes
    .map(
      (change) =>
        `${change.field}: ${defaultIfEmpty(change.from)} -> ${defaultIfEmpty(
          change.to
        )}`
    )
    .join(', ');

  return `${noteContent ?? ''}${
    changeStrings.length > 0 ? ` (${changeStrings})` : ''
  }`.trim();
};

/**
 * Converts a note to a string; particularly useful for exporting the note in the PRAS CSV exporter.
 *
 * @param note - note to convert.
 * @returns a string representation of the note.
 */
export const noteToString = (note: RelatedNote): string => {
  const noteWithCreatedAtAndUpdatedAtAsNumber =
    relatedNoteToRelatedNoteWithCreatedAtAndUpdatedAtAsNumber(note);

  const updatedAt =
    note.updatedAt === note.createdAt
      ? ''
      : ` (edited on ${format(
          noteWithCreatedAtAndUpdatedAtAsNumber.updatedAt,
          shiftDateTimeFormat
        )})`;

  return `${note.authorName ?? 'Unknown'} (${format(
    noteWithCreatedAtAndUpdatedAtAsNumber.createdAt,
    shiftDateTimeFormat
  )}): ${noteContentAndShiftChangesToString(
    noteWithCreatedAtAndUpdatedAtAsNumber.metadata?.shift?.changes ?? [],
    noteWithCreatedAtAndUpdatedAtAsNumber.content
  )}${updatedAt}`;
};

/**
 * Checks if the two dates are the same.
 * @param noteShortDateString - date in Australia/Sydney.
 * @param shiftDateString - date in UTC timezone.
 * @returns whether the two dates are the same.
 */

export const noteDateEqualsShiftDate = (
  noteShortDateString: string,
  shiftDateString: string
) =>
  noteShortDateString ===
  formatInTimeZone(shiftDateString, TIMEZONE, 'yyyy-MM-dd');
